// @flow
import * as React from 'react';
import { Box, Text } from 'grommet';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { isEmpty, min } from 'lodash';
import { FieldWrapper } from 'speed-js-react-web';
import NumberInput from 'speed-js-react-web/src/components/fields/NumberInput';
import lineMessages from 'esm-web-pro/messages/lineMessages';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import { ApiForm } from 'esm-web-pro/containers';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { Map } from 'esm-web-pro/containers/map/types';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import MapFiberConnection from 'esm-web-pro/containers/pages/network/objects/point12/MapFiberConnection';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import Select, { components } from 'react-select';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { CableFiberColor, FiberTextContainer } from 'esm-web-pro/containers/pages/network/objects/cable/CableFiber';


/* This is for the select SingleValue styling in the second fiber selection */
const Option = ({ children, ...props }: Object) => {
    const { data } = props;
    const { color, stripeCount, tubeNumber } = data;

    return (
        <components.Option {...props}>
            <FiberTextContainer>
                {(color)
                    && (
                        <CableFiberColor
                            color={color}
                            stripeCount={stripeCount}
                            tubeNumber={tubeNumber}
                        />
                    )
                }
                {/* {data.label} */}
                {children}

            </FiberTextContainer>
        </components.Option>
    );
};

/* This is for the select SingleValue styling in the second fiber selection */
const SingleValue = ({
    children,
    ...props
}: Object) => {
    const { data } = props;
    const { color, stripeCount, tubeNumber } = data;

    return (
        <components.SingleValue {...props}>
            <FiberTextContainer>
                {(color)
                    && (
                        <CableFiberColor
                            color={color}
                            stripeCount={stripeCount}
                            tubeNumber={tubeNumber}
                        />
                    )
                }
                {/* {data.label} */}
                {children}

            </FiberTextContainer>
        </components.SingleValue>
    );
};


export type Props = {
    container: Object,
    cables: Array<Object>,
    cable: Object,
    fiber: number,
    onClose: Function,
    onSave: Function,
    intl: IntlShape,
    freeFibers: Object,
    editor: BaseEditor,
    map: Map,
}

const SpliceFiberForm = (props: Props) => {
    const [secondCable, setSecondCable] = React.useState(null);
    const [secondFiber, setSecondFiber] = React.useState(null);
    const [range, setRange] = React.useState(1);
    const [rangeError, setRangeError] = React.useState<boolean>(false);

    const [fiberUuid, setFiberUuid] = React.useState(null);
    const [secondFiberUuid, setSecondFiberUuid] = React.useState(null);

    const {
        cables, cable, fiber, onClose, onSave, container, intl: { formatMessage }, freeFibers,
        editor, map: networkMap,
    } = props;

    const prevSecondCable = usePrevious(secondCable);
    const prevSecondFiber = usePrevious(secondFiber);

    const findFiberUuid = React.useCallback(() => {
        const { uuid } = cable.free_fibers.find((f) => f.sequence_number === fiber);
        setFiberUuid(uuid);
    }, []);

    const findSecondFiberUuid = React.useCallback(() => {
        if (!secondFiber || !secondCable) {
            setSecondFiberUuid(null);
        } else if ((prevSecondFiber !== secondFiber) || (prevSecondCable !== secondCable)) {
            const { uuid } = secondCable.free_fibers.find((f) => f.sequence_number === Number(secondFiber));
            setSecondFiberUuid(uuid);
        }
    }, [secondCable, secondFiber]);

    React.useEffect(() => {
        findFiberUuid();

        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [editor, findFiberUuid]);

    React.useEffect(() => {
        findSecondFiberUuid();
    }, [findSecondFiberUuid]);

    const getSpliceData = (values) => {
        if (!range || rangeError || !secondCable) return;
        // eslint-disable-next-line consistent-return
        return {
            ...values,
            container: container.uuid,
            splices: {
                first_cable: cable.uuid,
                second_cable: secondCable.uuid,
                first_cable_fiber_start: fiber,
                first_cable_fiber_end: fiber + range - 1,
                second_cable_fiber_start: Number(secondFiber),
                second_cable_fiber_end: Number(secondFiber) + range - 1,
            },
        };
    };

    const handleSpliceSave = () => {
        onSave();
    };

    const handleSecondCableChange = (uuid) => {
        if (!uuid) {
            setSecondFiber(null);
            setSecondCable(null);
            return;
        }

        const index = cables.findIndex((c) => c.uuid === uuid);

        if (cables[index].uuid !== cable.uuid) {
            setSecondFiber(null);
            setSecondCable(cables[index]);
        }
    };

    const handleSecondFiberChange = (choice) => {
        if (!choice) {
            setSecondFiber(null);
            return;
        }

        if (secondFiber !== choice) {
            setRange(1);
            setSecondFiber(choice);
        }
    };

    const renderSecondCableSelect = () => {
        const cableChoices = cables.reduce((choices, c) => {
            if (c.uuid !== cable.uuid && !isEmpty(freeFibers[c.id])) {
                choices[c.uuid] = `${formatMessage(
                    c.patch ? cableAndFiberMessages.patchCableLong : cableAndFiberMessages.cableLong,
                )} ${c.sequence} (${c.fiber_count}, 
                ${formatMessage(lineMessages.lineLength, { value: c.reserve })})`;
            }
            return choices;
        }, {});

        return (
            <FieldWrapper
                required
                label={(
                    <FormattedMessage
                        id="splicefiberfrom-secondcableselect-title"
                        defaultMessage="Cable"
                    />
                )}
            >
                <BaseSelect
                    emptyLabel={(
                        <FormattedMessage
                            id="splicefiberfrom-secondcableselect-select"
                            defaultMessage="Select cable"
                        />
                    )}
                    choices={cableChoices}
                    value={secondCable ? secondCable.uuid : null}
                    onChange={(choice) => {
                        handleSecondCableChange(choice);
                    }}
                />
            </FieldWrapper>
        );
    };

    const renderSecondFiberSelect = () => {
        // Map the free fibers inside the second cable to the format that the Select component expects
        // and add the cable fiber attributes color and stripe_count to the fiber choice so that the
        // color and stripe count can be displayed in the select SingleValue and Option components
        const fiberChoices = secondCable.free_fibers.map((choice) => ({
            value: choice.sequence_number,
            label: `${formatMessage(cableAndFiberMessages.fiberLong)} ${choice.sequence_number}`,
            color: choice.attributes?.color,
            stripeCount: choice.attributes?.stripe_count,
            tubeNumber: choice.attributes?.tube_number,
        }));

        if (secondFiber === null) {
            handleSecondFiberChange(fiberChoices[0].value);
        }

        return (
            <FieldWrapper
                required
                label={(
                    <FormattedMessage
                        id="splicefiberfrom-secondfiberselect-title"
                        defaultMessage="Fiber"
                    />
                )}
            >
                <Select
                    classNamePrefix="select"
                    placeholder=""
                    options={fiberChoices}
                    value={
                        fiberChoices?.find((
                            fiberChoice,
                        ) => fiberChoice.value === secondFiber)
                    }
                    onChange={
                        (choice) => {
                            handleSecondFiberChange(choice.value);
                        }
                    }
                    components={{ Option, SingleValue }}
                />
            </FieldWrapper>
        );
    };

    const renderRangeSelect = () => {
        const maxRange = min([freeFibers[cable.id][fiber], freeFibers[secondCable.id][secondFiber]]);

        return (
            <FieldWrapper
                key="splice-range-field"
                required
                label={(
                    <FormattedMessage
                        id="splicefiberfrom-range-title"
                        defaultMessage="Range"
                        values={{ first: 1, second: maxRange }}
                    />
                )}
            >
                <NumberInput
                    name="splicefiberfrom-range-input"
                    onChange={(e) => {
                        const choice = Number(e.target.value);
                        if (!choice || (choice && (choice > maxRange || choice < 0))) {
                            setRangeError(true);
                        } else {
                            setRangeError(false);
                        }
                        setRange(choice);
                    }}
                    min_value={1}
                    max_value={maxRange}
                    disabled={maxRange <= 1}
                    value={range || ''}
                />
                {rangeError && (
                    <Text
                        color="status-error"
                        alignSelf="center"
                        margin={{ top: '10px' }}
                    >
                        <FormattedMessage
                            id="splicefiberfrom-error-splice-range"
                            defaultMessage="The chosen value must be between {first} and {maxRange}."
                            values={{ first: 1, maxRange }}
                        />
                    </Text>
                )}
            </FieldWrapper>
        );
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Box height="100%">
            <MapFiberConnection
                initialFiber={fiberUuid}
                secondFiber={secondFiberUuid}
                editor={editor}
                map={networkMap}
            />
            <Box
                direction="row"
                align="center"
                height="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex justify="between">
                    <Box flex={{ shrink: 0 }} pad={{ bottom: 'small' }}>
                        <Box height="12px" flex={{ shrink: 0 }} />
                        <Box pad={{ horizontal: 'medium' }}>
                            {renderSecondCableSelect()}
                            <Box height="12px" flex={{ shrink: 0 }} />
                            {secondCable && renderSecondFiberSelect()}
                            <Box height="12px" flex={{ shrink: 0 }} />
                            {secondFiber && renderRangeSelect()}
                        </Box>
                    </Box>
                    {(secondCable && secondFiber && range && !rangeError) ? (
                        <ApiForm
                            key={cable.uuid}
                            resourceName="splice"
                            excludeFields={['container', 'model', 'attenuation', 'physical_circuit']}
                            height="100%"
                            onClose={onClose}
                            onSave={handleSpliceSave}
                            onSubmit={getSpliceData}
                        />
                    ) : (
                        <ActionButtons onClose={handleClose} onSave={false} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default injectIntl(SpliceFiberForm);
